import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Users,
  Activity,
  UserCheck,
  Layers,
  GraduationCap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  fetchDashboardAnalytics,
  clearDashboardError,
} from "../redux/slices/analyticsSlice";

function toCount(v) {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function Dashboard() {
  const dispatch = useDispatch();
  const { dashboard, loading, error } = useSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(clearDashboardError());
    dispatch(fetchDashboardAnalytics({}));
  }, [dispatch]);

  const overview = dashboard?.overview || {};
  const totalUsers = toCount(overview.totalUsers);
  const activeUsers = toCount(overview.activeUsers);
  const newUsers = toCount(overview.newUsers);
  const totalEvents = toCount(overview.totalEvents);

  const topEvents = dashboard?.topEvents || [];
  const eventsByPlatform = dashboard?.eventsByPlatform || [];

  const stats = [
    {
      title: "Total users",
      value: String(totalUsers),
      icon: Users,
      hint: "Registered accounts",
    },
    {
      title: "Active (30 days)",
      value: String(activeUsers),
      icon: UserCheck,
      hint: "Logged in recently",
    },
    {
      title: "New users (range)",
      value: String(newUsers),
      icon: Users,
      hint: "Matches selected date filter when used",
    },
    {
      title: "Analytics events",
      value: String(totalEvents),
      icon: Activity,
      hint: "Tracked app events in range",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">
          Live metrics from analytics and user records
        </p>
      </div>

      {error && (
        <p className="text-sm text-destructive">
          {error} — check that you are logged in as admin.
        </p>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {loading && !dashboard ? "…" : stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{stat.hint}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top events</CardTitle>
            <CardDescription>Frequently tracked event names</CardDescription>
          </CardHeader>
          <CardContent>
            {!loading &&
              topEvents.length > 0 &&
              topEvents.map((row, idx) => {
                const raw = row.count ?? row.get?.("count");
                return (
                  <div
                    key={`${row.event_name ?? idx}`}
                    className="flex justify-between text-sm py-1 border-b border-border/60 last:border-0"
                  >
                    <span className="truncate pr-2">
                      {row.event_name || "—"}
                    </span>
                    <span className="text-muted-foreground shrink-0">
                      {toCount(raw)}
                    </span>
                  </div>
                );
              })}
            {!loading && topEvents.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No analytics events recorded yet.
              </div>
            )}
            {loading && !dashboard && (
              <div className="text-sm text-muted-foreground">Loading…</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick actions</CardTitle>
            <CardDescription>Jump to common admin areas</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Button variant="outline" asChild className="justify-start">
              <Link to="/users">
                <Users className="w-4 h-4 mr-2" />
                User directory
              </Link>
            </Button>
            <Button variant="outline" asChild className="justify-start">
              <Link to="/questions">
                <Layers className="w-4 h-4 mr-2" />
                Question bank
              </Link>
            </Button>
            <Button variant="outline" asChild className="justify-start">
              <Link to="/courses">
                <GraduationCap className="w-4 h-4 mr-2" />
                Courses
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {eventsByPlatform.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Events by platform</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {eventsByPlatform.map((row, idx) => (
                <div
                  key={`${row.platform ?? idx}`}
                  className="rounded-lg border p-3 text-sm"
                >
                  <div className="font-medium capitalize">
                    {row.platform || "unknown"}
                  </div>
                  <div className="text-muted-foreground">
                    {toCount(row.count ?? row.get?.("count"))} events
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Dashboard;
